import React, { useEffect, useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import RankingsTab from "../components/RankingsTab";
import SectorsTab from "../components/SectorsTab";
import AiVisibilityTab from "../components/AiVisibilityTab";
import DirectoryBlastTab from "../components/DirectoryBlastTab";
import PricingTab from "../components/PricingTab";
import CouponsTab from "../components/CouponsTab";
import { toast } from "sonner";
import {
  adminAssignOrder,
  adminCreatePoster,
  adminCreateRider,
  adminDeletePoster,
  adminListNotifications,
  adminListOrders,
  adminListPosters,
  adminListReferrals,
  adminListRiders,
  adminListSubscriptions,
  adminLogin,
  adminStats,
  adminUpdateOrder,
  adminUpdatePoster,
  adminUpdateRider,
  assetUrl,
  STATUS_COLORS,
  STATUS_LABELS,
} from "../lib/api";

const STATUSES = ["pending", "picked_up", "washing", "out_for_delivery", "delivered", "cancelled"];

function StatCard({ label, value, testId }) {
  return (
    <div className="border-r border-b border-rw-ink/10 p-6" data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-2">{value}</div>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("rw_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("orders"); // orders | subscriptions | notifications | referrals
  const [subs, setSubs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [refs, setRefs] = useState([]);
  const [posters, setPosters] = useState([]);
  const [riders, setRiders] = useState([]);
  const [riderForm, setRiderForm] = useState({ name: "", phone: "", pin: "", sectors: [] });
  const [creatingRider, setCreatingRider] = useState(false);
  const [posterForm, setPosterForm] = useState({
    kind: "banner",
    title: "",
    subtitle: "",
    link_url: "",
    active: true,
    start_date: "",
    end_date: "",
    order: 0,
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  const attemptLogin = async (k) => {
    setLoading(true);
    try {
      await adminLogin(k);
      localStorage.setItem("rw_admin_key", k);
      setKey(k);
      setAuthed(true);
      await refresh(k);
    } catch (e) {
      toast.error("Invalid admin key");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async (k = key) => {
    try {
      const [o, s, sb, n, rf, po, rd] = await Promise.all([
        adminListOrders(k),
        adminStats(k),
        adminListSubscriptions(k),
        adminListNotifications(k),
        adminListReferrals(k),
        adminListPosters(k),
        adminListRiders(k),
      ]);
      setOrders(o.orders || []);
      setStats(s);
      setSubs(sb.subscriptions || []);
      setNotifs(n.notifications || []);
      setRefs(rf.referrals || []);
      setPosters(po.posters || []);
      setRiders(rd.riders || []);
    } catch (e) {
      toast.error("Session expired. Please sign in again.");
      setAuthed(false);
      localStorage.removeItem("rw_admin_key");
    }
  };

  const createRider = async (e) => {
    e.preventDefault();
    if (!riderForm.name || !riderForm.phone || !riderForm.pin) {
      toast.error("Name, phone and PIN are required");
      return;
    }
    setCreatingRider(true);
    try {
      const r = await adminCreateRider(key, {
        name: riderForm.name.trim(),
        phone: riderForm.phone.trim(),
        pin: riderForm.pin.trim(),
        sectors: riderForm.sectors,
      });
      toast.success(`Rider ${r.rider.rider_code} created`);
      setRiderForm({ name: "", phone: "", pin: "", sectors: [] });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create rider");
    } finally {
      setCreatingRider(false);
    }
  };

  const toggleRiderActive = async (code, active) => {
    try {
      await adminUpdateRider(key, code, { active });
      refresh();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const assignOrder = async (code, rider_code) => {
    try {
      await adminAssignOrder(key, code, rider_code || null);
      toast.success(rider_code ? `Assigned to ${rider_code}` : "Unassigned");
      refresh();
    } catch (e) {
      toast.error("Failed to assign");
    }
  };

  const uploadPoster = async (e) => {
    e.preventDefault();
    if (!posterForm.file) { toast.error("Choose an image"); return; }
    const fd = new FormData();
    fd.append("file", posterForm.file);
    fd.append("kind", posterForm.kind);
    if (posterForm.title) fd.append("title", posterForm.title);
    if (posterForm.subtitle) fd.append("subtitle", posterForm.subtitle);
    if (posterForm.link_url) fd.append("link_url", posterForm.link_url);
    fd.append("active", String(posterForm.active));
    if (posterForm.start_date) fd.append("start_date", posterForm.start_date);
    if (posterForm.end_date) fd.append("end_date", posterForm.end_date);
    fd.append("order", String(posterForm.order || 0));
    setUploading(true);
    try {
      await adminCreatePoster(key, fd);
      toast.success("Poster uploaded");
      setPosterForm({ kind: "banner", title: "", subtitle: "", link_url: "", active: true, start_date: "", end_date: "", order: 0, file: null });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const togglePoster = async (id, patch) => {
    try {
      await adminUpdatePoster(key, id, patch);
      refresh();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const removePoster = async (id) => {
    if (!window.confirm("Delete this poster?")) return;
    try {
      await adminDeletePoster(key, id);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  useEffect(() => {
    if (key) attemptLogin(key);
  }, []);

  const updateStatus = async (code, patch) => {
    try {
      await adminUpdateOrder(key, code, patch);
      toast.success(`Order ${code} updated`);
      refresh();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="admin-login-page">
        <Seo title="Admin Console" description="RIZ N WASH internal admin console." path="/admin" noindex={true} />
        <Nav />
        <div className="max-w-md mx-auto px-6 md:px-10 pt-20">
          <div className="overline mb-3">Admin</div>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight">
            Team console
          </h1>
          <p className="mt-3 text-rw-ink2">Enter your admin key to manage orders.</p>
          <form
            className="mt-8 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 space-y-4"
            onSubmit={(e) => { e.preventDefault(); attemptLogin(key); }}
            data-testid="admin-login-form"
          >
            <label className="block">
              <span className="overline">Admin key</span>
              <input
                type="password"
                className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-key-input"
              />
            </label>
            <button
              type="submit"
              disabled={loading || !key}
              className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
              data-testid="admin-login-btn"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
        <Footer />
      </div>
    );
  }

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="admin-dashboard-page">
      <Seo title="Admin Dashboard" description="RIZ N WASH internal admin dashboard." path="/admin" noindex={true} />
      <Nav />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <div className="overline mb-2">Admin dashboard</div>
            <h1 className="font-display text-3xl md:text-5xl font-light tracking-tight">Orders overview</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => refresh()}
              className="rounded-full border border-rw-ink/20 px-4 py-2 text-sm hover:bg-rw-bg3"
              data-testid="admin-refresh-btn"
            >
              Refresh
            </button>
            <button
              onClick={() => { localStorage.removeItem("rw_admin_key"); setAuthed(false); setKey(""); }}
              className="rounded-full border border-rw-ink/20 px-4 py-2 text-sm hover:bg-rw-bg3"
              data-testid="admin-logout-btn"
            >
              Sign out
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-7 border-t border-l border-rw-ink/10 bg-rw-bg2 rounded-2xl overflow-hidden">
            <StatCard label="Total orders" value={stats.total_orders} testId="stat-total" />
            <StatCard label="Pending" value={stats.pending} testId="stat-pending" />
            <StatCard label="Active" value={stats.active} testId="stat-active" />
            <StatCard label="Delivered" value={stats.delivered} testId="stat-delivered" />
            <StatCard label="Revenue" value={`₹${stats.revenue.toFixed(0)}`} testId="stat-revenue" />
            <StatCard label="Subscriptions" value={stats.active_subscriptions || 0} testId="stat-subs" />
            <StatCard label="Referrals issued" value={stats.referrals_issued || 0} testId="stat-referrals" />
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-2 border-b border-rw-ink/10 pb-3">
          {[
            ["orders", "Orders"],
            ["pricing", "Pricing"],
            ["coupons", "Coupons"],
            ["subscriptions", "Subscriptions"],
            ["notifications", "Notifications"],
            ["referrals", "Referrals"],
            ["posters", "Posters"],
            ["riders", "Riders"],
            ["sectors", "Heat map"],
            ["rankings", "Rankings"],
            ["aiv", "AI visibility"],
            ["directories", "Directory blast"],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-sm font-semibold rounded-full ${
                tab === id ? "bg-rw-ink text-white" : "text-rw-ink2 hover:bg-rw-bg3"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
        <>
        <div className="mt-8 flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${
                filter === s ? "bg-rw-ink text-white border-rw-ink" : "border-rw-ink/20 text-rw-ink2 hover:bg-rw-bg3"
              }`}
              data-testid={`filter-${s}`}
            >
              {s === "all" ? "All" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-rw-ink/10 bg-rw-bg2 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-orders-table">
              <thead className="bg-rw-bg3 text-left text-rw-ink2">
                <tr>
                  <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Order</th>
                  <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Customer</th>
                  <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Pickup</th>
                  <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Total</th>
                  <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Status</th>
                  <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Payment</th>
                  <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-rw-ink2">No orders here yet.</td>
                  </tr>
                )}
                {filtered.map((o) => (
                  <tr key={o.order_code} className="border-b border-rw-ink/10 hover:bg-rw-bg3/50" data-testid={`admin-row-${o.order_code}`}>
                    <td className="px-4 py-3 font-mono font-semibold">{o.order_code}</td>
                    <td className="px-4 py-3">
                      <div>{o.customer_name}</div>
                      <div className="text-rw-ink3 text-xs">{o.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{o.pickup_date}</div>
                      <div className="text-rw-ink3 text-xs">{o.pickup_slot}</div>
                    </td>
                    <td className="px-4 py-3 font-display">₹{o.total}</td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-1 rounded-full text-xs"
                        style={{ background: STATUS_COLORS[o.status]?.bg || "#eee", color: "#1A1A24" }}
                      >
                        {STATUS_LABELS[o.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs uppercase tracking-widest">{o.payment_status}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 items-center">
                        <select
                          value={o.status}
                          onChange={(e) => updateStatus(o.order_code, { status: e.target.value })}
                          className="rounded-md border border-rw-ink/20 bg-white px-2 py-1 text-xs"
                          data-testid={`admin-status-select-${o.order_code}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                        {o.payment_status !== "paid" ? (
                          <button
                            onClick={() => updateStatus(o.order_code, { status: o.status, payment_status: "paid" })}
                            className="text-xs px-3 py-1 rounded-full bg-rw-mint hover:bg-rw-sage border border-rw-ink/10"
                            data-testid={`admin-mark-paid-${o.order_code}`}
                          >
                            Mark paid
                          </button>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-rw-sage/40">Paid</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}

        {tab === "subscriptions" && (
          <div className="mt-6 rounded-2xl border border-rw-ink/10 bg-rw-bg2 overflow-hidden" data-testid="admin-subs-panel">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-rw-bg3 text-left text-rw-ink2">
                  <tr>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Sub</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Customer</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Plan</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Weekly</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Starts</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Referral</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-rw-ink2">No subscriptions yet.</td></tr>
                  )}
                  {subs.map((s) => (
                    <tr key={s.sub_code} className="border-b border-rw-ink/10" data-testid={`admin-sub-row-${s.sub_code}`}>
                      <td className="px-4 py-3 font-mono font-semibold">{s.sub_code}</td>
                      <td className="px-4 py-3">
                        <div>{s.customer_name}</div>
                        <div className="text-rw-ink3 text-xs">{s.phone}</div>
                      </td>
                      <td className="px-4 py-3">{s.plan_name}</td>
                      <td className="px-4 py-3 font-display">₹{s.weekly_total}</td>
                      <td className="px-4 py-3">{s.start_date}</td>
                      <td className="px-4 py-3 font-mono text-xs">{s.referral_code || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "notifications" && (
          <div className="mt-6 rounded-2xl border border-rw-ink/10 bg-rw-bg2 overflow-hidden" data-testid="admin-notifs-panel">
            <div className="p-4 border-b border-rw-ink/10 text-xs text-rw-ink2 flex items-center justify-between">
              <span>Twilio channel: WhatsApp · Status: <span className="font-semibold text-rw-ink">{notifs[0]?.status === "sent" ? "LIVE" : "MOCKED"}</span> (set TWILIO_ENABLED=true in .env once credentials are configured)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-rw-bg3 text-left text-rw-ink2">
                  <tr>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">When</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Kind</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">To</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Message</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {notifs.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-rw-ink2">No notifications sent yet.</td></tr>
                  )}
                  {notifs.map((n) => (
                    <tr key={n.id} className="border-b border-rw-ink/10" data-testid={`admin-notif-row-${n.id}`}>
                      <td className="px-4 py-3 text-xs text-rw-ink2 whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs uppercase tracking-widest">{n.kind}</td>
                      <td className="px-4 py-3 font-mono text-xs">{n.to}</td>
                      <td className="px-4 py-3 max-w-md text-rw-ink2">{n.body}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${n.status === "sent" ? "bg-rw-sage/40" : "bg-rw-mint"}`}>
                          {n.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "referrals" && (
          <div className="mt-6 rounded-2xl border border-rw-ink/10 bg-rw-bg2 overflow-hidden" data-testid="admin-refs-panel">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-rw-bg3 text-left text-rw-ink2">
                  <tr>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Code</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Phone</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10"># Referred</th>
                    <th className="px-4 py-3 font-semibold border-b border-rw-ink/10">Credit balance</th>
                  </tr>
                </thead>
                <tbody>
                  {refs.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-rw-ink2">No referral codes issued yet.</td></tr>
                  )}
                  {refs.map((r) => (
                    <tr key={r.code} className="border-b border-rw-ink/10" data-testid={`admin-ref-row-${r.code}`}>
                      <td className="px-4 py-3 font-mono font-semibold">{r.code}</td>
                      <td className="px-4 py-3">{r.phone}</td>
                      <td className="px-4 py-3">{r.referred_count || 0}</td>
                      <td className="px-4 py-3 font-display">₹{(r.credit_balance || 0).toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "posters" && (
          <div className="mt-6 grid lg:grid-cols-3 gap-6" data-testid="admin-posters-panel">
            {/* Upload form */}
            <form
              onSubmit={uploadPoster}
              className="lg:col-span-1 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5 space-y-3 h-fit"
              data-testid="poster-upload-form"
            >
              <div className="font-display text-xl">Upload new poster</div>

              <label className="block">
                <span className="overline">Kind</span>
                <select
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                  value={posterForm.kind}
                  onChange={(e) => setPosterForm({ ...posterForm, kind: e.target.value })}
                  data-testid="poster-input-kind"
                >
                  <option value="banner">Banner (top strip)</option>
                  <option value="carousel">Carousel (home section)</option>
                  <option value="popup">Popup (first-visit modal)</option>
                </select>
              </label>

              <label className="block">
                <span className="overline">Image (≤5 MB · PNG/JPG/WEBP)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPosterForm({ ...posterForm, file: e.target.files?.[0] || null })}
                  className="mt-1.5 w-full text-sm"
                  data-testid="poster-input-file"
                />
              </label>

              <label className="block">
                <span className="overline">Title</span>
                <input
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                  value={posterForm.title}
                  onChange={(e) => setPosterForm({ ...posterForm, title: e.target.value })}
                  placeholder="e.g. Diwali offer"
                  data-testid="poster-input-title"
                />
              </label>

              <label className="block">
                <span className="overline">Subtitle</span>
                <input
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                  value={posterForm.subtitle}
                  onChange={(e) => setPosterForm({ ...posterForm, subtitle: e.target.value })}
                  placeholder="20% off your first pickup"
                  data-testid="poster-input-subtitle"
                />
              </label>

              <label className="block">
                <span className="overline">Link URL (optional)</span>
                <input
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                  value={posterForm.link_url}
                  onChange={(e) => setPosterForm({ ...posterForm, link_url: e.target.value })}
                  placeholder="/book or https://…"
                  data-testid="poster-input-link"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">Start date</span>
                  <input
                    type="date"
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                    value={posterForm.start_date}
                    onChange={(e) => setPosterForm({ ...posterForm, start_date: e.target.value })}
                    data-testid="poster-input-start"
                  />
                </label>
                <label className="block">
                  <span className="overline">End date</span>
                  <input
                    type="date"
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                    value={posterForm.end_date}
                    onChange={(e) => setPosterForm({ ...posterForm, end_date: e.target.value })}
                    data-testid="poster-input-end"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">Order</span>
                  <input
                    type="number"
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                    value={posterForm.order}
                    onChange={(e) => setPosterForm({ ...posterForm, order: parseInt(e.target.value || 0, 10) })}
                    data-testid="poster-input-order"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input
                    type="checkbox"
                    checked={posterForm.active}
                    onChange={(e) => setPosterForm({ ...posterForm, active: e.target.checked })}
                    data-testid="poster-input-active"
                  />
                  Active
                </label>
              </div>

              <button
                type="submit"
                disabled={uploading || !posterForm.file}
                className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
                data-testid="poster-upload-btn"
              >
                {uploading ? "Uploading…" : "Upload poster"}
              </button>
            </form>

            {/* Posters list */}
            <div className="lg:col-span-2 space-y-3">
              {posters.length === 0 && (
                <div className="rounded-2xl border border-dashed border-rw-ink/20 p-10 text-center text-rw-ink2">
                  No posters yet. Upload your first one on the left.
                </div>
              )}
              {posters.map((p) => (
                <div key={p.id} className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-4 flex gap-4" data-testid={`poster-row-${p.id}`}>
                  <img src={assetUrl(p.image_url)} alt="" className="w-32 h-20 object-cover rounded-md flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded-full bg-rw-bg3">{p.kind}</span>
                      {p.title && <span className="font-display text-lg">{p.title}</span>}
                    </div>
                    {p.subtitle && <div className="text-sm text-rw-ink2 truncate">{p.subtitle}</div>}
                    <div className="text-xs text-rw-ink3 mt-1 flex flex-wrap gap-3">
                      {p.link_url && <span className="truncate">→ {p.link_url}</span>}
                      {p.start_date && <span>from {p.start_date}</span>}
                      {p.end_date && <span>until {p.end_date}</span>}
                      <span>order: {p.order || 0}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => togglePoster(p.id, { active: !p.active })}
                      className={`text-xs rounded-full px-3 py-1.5 font-semibold ${p.active ? "bg-rw-mint" : "border border-rw-ink/20"}`}
                      data-testid={`poster-toggle-${p.id}`}
                    >
                      {p.active ? "Active" : "Off"}
                    </button>
                    <button
                      onClick={() => removePoster(p.id)}
                      className="text-xs rounded-full bg-rw-peach/60 px-3 py-1.5 font-semibold"
                      data-testid={`poster-delete-${p.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "riders" && (
          <div className="mt-6 grid lg:grid-cols-3 gap-6" data-testid="admin-riders-panel">
            <form
              onSubmit={createRider}
              className="lg:col-span-1 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5 space-y-3 h-fit"
              data-testid="admin-rider-form"
            >
              <div className="font-display text-xl">Add rider</div>
              <label className="block">
                <span className="overline">Name</span>
                <input
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                  value={riderForm.name}
                  onChange={(e) => setRiderForm({ ...riderForm, name: e.target.value })}
                  placeholder="Ramesh Kumar"
                  data-testid="admin-rider-name"
                />
              </label>
              <label className="block">
                <span className="overline">Phone</span>
                <input
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                  value={riderForm.phone}
                  onChange={(e) => setRiderForm({ ...riderForm, phone: e.target.value })}
                  placeholder="98xxx xxxxx"
                  data-testid="admin-rider-phone"
                />
              </label>
              <label className="block">
                <span className="overline">4–6 digit PIN</span>
                <input
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus font-mono"
                  value={riderForm.pin}
                  onChange={(e) => setRiderForm({ ...riderForm, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  placeholder="1234"
                  data-testid="admin-rider-pin"
                />
              </label>
              <div>
                <span className="overline">Sectors</span>
                <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
                  {(orders.length > 0 ? [...new Set(orders.map((o) => o.sector).filter(Boolean))] : []).map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setRiderForm((f) => ({
                        ...f,
                        sectors: f.sectors.includes(s) ? f.sectors.filter((x) => x !== s) : [...f.sectors, s],
                      }))}
                      className={`text-[10px] rounded-full px-2 py-1 border ${
                        riderForm.sectors.includes(s)
                          ? "bg-rw-ink text-white border-rw-ink"
                          : "border-rw-ink/20 text-rw-ink2"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-rw-ink3 mt-1">Empty = citywide.</p>
              </div>
              <button
                type="submit"
                disabled={creatingRider}
                className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
                data-testid="admin-rider-submit"
              >
                {creatingRider ? "Creating…" : "Create rider"}
              </button>
              <p className="text-xs text-rw-ink3">Rider signs in at <span className="font-mono">/rider</span> with the ID (auto-generated) + PIN, or their phone + a password they set themselves.</p>
            </form>

            <div className="lg:col-span-2 space-y-3">
              {riders.length === 0 && (
                <div className="rounded-2xl border border-dashed border-rw-ink/20 p-10 text-center text-rw-ink2">
                  No riders yet. Create your first one on the left.
                </div>
              )}
              {riders.map((r) => (
                <div key={r.rider_code} className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-rider-row-${r.rider_code}`}>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono font-semibold">{r.rider_code}</span>
                      <span className="font-display text-lg">{r.name}</span>
                    </div>
                    <div className="text-xs text-rw-ink2 mt-1">{r.phone}</div>
                    {r.sectors?.length > 0 && (
                      <div className="text-xs text-rw-ink3 mt-1 truncate">Sectors: {r.sectors.join(", ")}</div>
                    )}
                  </div>
                  <button
                    onClick={() => toggleRiderActive(r.rider_code, !r.active)}
                    className={`text-xs rounded-full px-3 py-1.5 font-semibold ${r.active ? "bg-rw-mint" : "border border-rw-ink/20"}`}
                    data-testid={`admin-rider-toggle-${r.rider_code}`}
                  >
                    {r.active ? "Active" : "Inactive"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "sectors" && <SectorsTab adminKey={key} />}
        {tab === "rankings" && <RankingsTab adminKey={key} />}
        {tab === "aiv" && <AiVisibilityTab adminKey={key} />}
        {tab === "directories" && <DirectoryBlastTab adminKey={key} />}
        {tab === "pricing" && <PricingTab adminKey={key} />}
        {tab === "coupons" && <CouponsTab adminKey={key} />}
      </div>

      <Footer />
    </div>
  );
}
